import { FC, memo } from 'react';
import type { Entry, Resume as ResumeData } from '../types/cv';

import { Header } from './Header';
import { Summary } from './Summary';
import { WorkExperience } from './WorkExperience';
import { Projects } from './Projects';
import { Skills } from './Skills';
import { Education } from './Education';
import { Awards } from './Awards';
import { Certificates } from './Certificates';
import { Volunteer } from './Volunteer';
import { Publications } from './Publications';
import { Languages } from './Languages';
import { Interests } from './Interests';
import { References } from './References';

/**
 * Projects carrying `entity` belong under that job (see WorkExperience);
 * everything else stays a standalone project.
 */
const groupByEntity = (projects: Entry[] = []): Record<string, Entry[]> =>
  projects.reduce<Record<string, Entry[]>>((groups, project) => {
    if (!project.entity) return groups;
    (groups[project.entity] ??= []).push(project);
    return groups;
  }, {});

/**
 * Single column, top to bottom. The section order below is the reading order a
 * CV parser sees, so it is also the order of the page — reorder here and
 * nothing else needs to change.
 */
export const Resume: FC<{ resume: ResumeData }> = memo(({ resume }) => {
  const {
    basics,
    work,
    projects,
    skills,
    education,
    awards,
    certificates,
    volunteer,
    publications,
    languages,
    interests,
    references,
    meta,
  } = resume;

  if (!basics) return null;

  const has = <T,>(list?: T[]): list is T[] => Array.isArray(list) && list.length > 0;

  const engagements = groupByEntity(projects);
  const standaloneProjects = (projects ?? []).filter(project => !project.entity);

  return (
    <main className="cv-page">
      <Header basics={basics} meta={meta} />

      <div className="cv-body">
        {basics.summary && <Summary summary={basics.summary} title={meta?.summaryTitle} />}
        {has(work) && (
          <WorkExperience
            work={work}
            title={meta?.workTitle}
            engagements={engagements}
            engagementsTitle={meta?.engagementsTitle}
          />
        )}
        {has(standaloneProjects) && (
          <Projects projects={standaloneProjects} title={meta?.projectsTitle} />
        )}
        {has(skills) && <Skills skills={skills} title={meta?.skillsTitle} />}
        {has(education) && <Education education={education} title={meta?.educationTitle} />}
        {has(awards) && <Awards awards={awards} title={meta?.awardsTitle} oneLine />}
        {has(certificates) && (
          <Certificates certificates={certificates} title={meta?.certificatesTitle} oneLine />
        )}
        {has(volunteer) && <Volunteer volunteer={volunteer} title={meta?.volunteerTitle} />}
        {has(publications) && (
          <Publications publications={publications} title={meta?.publicationsTitle} oneLine />
        )}
        {has(languages) && <Languages languages={languages} title={meta?.languagesTitle} />}
        {has(interests) && <Interests interests={interests} title={meta?.interestsTitle} />}
        {has(references) && <References references={references} />}
      </div>
    </main>
  );
});

Resume.displayName = 'Resume';

export default Resume;
